import { type KnowledgeItemDto, updateKnowledgeInput } from '@fos/shared/contracts/knowledge';
import { type FormEvent, useRef, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { DateInput } from '../../../ui/DateTimeInput';
import { Field } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { toast } from '../../../ui/Toast';
import { NoteEditor } from './NoteEditor';

type FieldKey = 'title' | 'bodyMd' | 'url' | 'decidedOn';

function toErrors(issues: Array<{ path: string; message: string }>): Partial<Record<FieldKey, string>> {
  const out: Partial<Record<FieldKey, string>> = {};
  for (const issue of issues) {
    const key = issue.path.split('.')[0] as FieldKey;
    if (['title', 'bodyMd', 'url', 'decidedOn'].includes(key) && !out[key]) out[key] = issue.message;
  }
  return out;
}

/**
 * Edycja rekordu wiedzy w miejscu (tytuł, treść Markdown, adres linku, data decyzji). Zmiana treści = nowa wersja;
 * `version` chroni przed nadpisaniem cudzej zmiany (CONFLICT → „Wczytaj najnowszą”).
 */
export function KnowledgeEditForm({
  item,
  onDone,
  onReload,
}: {
  item: KnowledgeItemDto;
  onDone: () => void;
  /** CONFLICT: odśwież rekord (szkic zostaje utracony) */
  onReload: () => void;
}) {
  const utils = trpc.useUtils();
  const formRef = useRef<HTMLFormElement>(null);
  const [title, setTitle] = useState(item.title);
  const [bodyMd, setBodyMd] = useState(item.bodyMd);
  const [url, setUrl] = useState(item.url ?? '');
  const [decidedOn, setDecidedOn] = useState(item.decidedOn ?? '');
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [conflict, setConflict] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  useFocusFirstInvalid(formRef, errors);

  const update = trpc.knowledge.update.useMutation({
    onSuccess: (updated) => {
      const newVersion = updated.currentVersion !== item.currentVersion;
      toast.success('Zapisano', {
        description: newVersion ? `Wersja ${updated.currentVersion}` : 'Treść bez zmian, ta sama wersja.',
      });
      utils.knowledge.get.setData({ id: item.id }, updated);
      void utils.knowledge.list.invalidate();
      void utils.knowledge.listDecisions.invalidate();
      void utils.knowledge.listVersions.invalidate({ id: item.id });
      onDone();
    },
    onError: (err) => {
      if (appErrorCode(err) === 'CONFLICT') {
        setConflict(true);
        return;
      }
      const fieldErrors = toErrors(validationIssues(err));
      if (Object.keys(fieldErrors).length > 0) setErrors(fieldErrors);
      else setFormError(errorMessage(err));
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const input = {
      id: item.id,
      version: item.version,
      title: title.trim(),
      bodyMd,
      ...(item.kind === 'link' ? { url: url.trim() || null } : {}),
      ...(item.kind === 'decision' ? { decidedOn: decidedOn || null } : {}),
    };
    const parsed = updateKnowledgeInput.safeParse(input);
    if (!parsed.success) {
      setErrors(toErrors(parsed.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message }))));
      return;
    }
    setErrors({});
    update.mutate(input);
  };

  return (
    <form ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-4" aria-label="Edycja">
      {conflict && (
        <FormMessage>
          <span className="flex flex-col gap-2">
            Ktoś zapisał zmiany w międzyczasie. Wczytaj najnowszą wersję i wprowadź swoje zmiany ponownie.
            <span>
              <Button variant="secondary" size="s" onClick={onReload}>
                Wczytaj najnowszą wersję
              </Button>
            </span>
          </span>
        </FormMessage>
      )}
      {formError && <FormMessage>{formError}</FormMessage>}
      <Field label="Tytuł" error={errors.title}>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} />
      </Field>
      {item.kind === 'decision' && (
        <Field label="Data decyzji" error={errors.decidedOn}>
          <DateInput value={decidedOn} onValueChange={setDecidedOn} />
        </Field>
      )}
      {item.kind === 'link' && (
        <Field label="Adres" error={errors.url}>
          <Input type="url" inputMode="url" value={url} onChange={(e) => setUrl(e.target.value)} />
        </Field>
      )}
      <NoteEditor
        label={item.kind === 'note' ? 'Treść' : 'Opis'}
        value={bodyMd}
        onChange={setBodyMd}
        rows={16}
        error={errors.bodyMd}
      />
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="ghost" onClick={onDone} disabled={update.isPending}>
          Anuluj
        </Button>
        <Button variant="primary" type="submit" loading={update.isPending} disabled={conflict}>
          Zapisz
        </Button>
      </div>
    </form>
  );
}
